import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAstrologer, getAstrologerActivity, getAstrologerSchedule, updateAstrologerApproval, updateAstrologer,
  resolveMediaUrl, setAstrologerCredentials,
  getAdminWithdrawals, getAdminAppointments,
} from '../../api';
import {
  PageHeader, StatusBadge, Table, Pagination, EmptyState, TableSkeleton,
  UserLink,
} from '../../components/ui';
import Modal from '../../components/ui/Modal';
import ImageViewer from '../../components/ui/ImageViewer';
import {
  formatCurrency, formatDate, formatDateTime, formatDuration,
} from '../../utils/format';
import {
  KeyRound, ArrowLeft, Ban, CheckCircle, Clock, DollarSign, MessageSquare, PhoneCall,
  Video, RefreshCw, Star, User, Users, Wallet, Mail, Phone, Activity,
  ShieldCheck, XCircle, Briefcase, Copy, Eye, Calendar, CreditCard, Tag, Save,
} from 'lucide-react';
import toast from 'react-hot-toast';

type TabKey = 'overview' | 'profile' | 'availability' | 'pricing' | 'earnings' | 'withdrawals' | 'consultations' | 'reviews';

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'overview', label: 'Overview', icon: <Activity size={14} /> },
  { key: 'profile', label: 'Profile', icon: <User size={14} /> },
  { key: 'availability', label: 'Availability', icon: <Calendar size={14} /> },
  { key: 'pricing', label: 'Pricing', icon: <Tag size={14} /> },
  { key: 'earnings', label: 'Earnings', icon: <Wallet size={14} /> },
  { key: 'withdrawals', label: 'Withdrawals', icon: <CreditCard size={14} /> },
  { key: 'consultations', label: 'Consultations', icon: <MessageSquare size={14} /> },
  { key: 'reviews', label: 'Reviews', icon: <Star size={14} /> },
];

const TYPE_ICON: Record<string, React.ReactNode> = {
  chat: <MessageSquare size={13} className="text-blue-600" />,
  call: <PhoneCall size={13} className="text-green-600" />,
  video: <Video size={13} className="text-purple-600" />,
};

function Avatar({
  name, image, size = 64, onClick,
}: { name?: string; image?: string; size?: number; onClick?: () => void }) {
  const src = resolveMediaUrl(image);
  if (src) {
    const img = (
      <img
        src={src}
        alt={name || 'Astrologer'}
        className="rounded-full object-cover border-2 border-white shadow"
        style={{ width: size, height: size }}
      />
    );
    if (!onClick) return img;
    return (
      <button
        type="button"
        onClick={onClick}
        className="relative group rounded-full focus:outline-none focus:ring-2 focus:ring-primary-400"
        title="View profile photo"
      >
        {img}
        <span className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/35 transition flex items-center justify-center">
          <Eye size={Math.max(14, size / 5)} className="text-white opacity-0 group-hover:opacity-100" />
        </span>
      </button>
    );
  }
  return (
    <div
      className="rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold shadow"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {(name?.[0] || '?').toUpperCase()}
    </div>
  );
}

function StatTile({
  label, value, icon, color = 'text-gray-900', sub,
}: {
  label: string; value: React.ReactNode; icon: React.ReactNode; color?: string; sub?: string;
}) {
  return (
    <div className="card p-4 flex items-start gap-3">
      <div className={`w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center ${color}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p className={`text-lg font-bold mt-0.5 truncate ${color}`}>{value}</p>
        {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function InfoCell({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3">
      <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">{label}</p>
      <p className="text-sm font-semibold text-gray-900 mt-1 break-words">{value || '—'}</p>
    </div>
  );
}

export default function AstrologerDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [tab, setTab] = useState<TabKey>('overview');
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [approveModal, setApproveModal] = useState<string | null>(null);
  const [credModal, setCredModal] = useState(false);
  const [credResult, setCredResult] = useState<{ loginId: string; password: string } | null>(null);
  const [credPassword, setCredPassword] = useState('');
  const [approvalNote, setApprovalNote] = useState('');
  const [photoViewer, setPhotoViewer] = useState<string | null>(null);
  const [priceForm, setPriceForm] = useState({
    chatPrice: '',
    callPrice: '',
    videoPrice: '',
    chatMinMinutes: '5',
    callMinMinutes: '5',
    videoMinMinutes: '10',
  });

  const { data: detail, isLoading, refetch } = useQuery({
    queryKey: ['admin-astro-detail', id],
    queryFn: async () => {
      const r = await getAstrologer(id!);
      return r.data.data;
    },
    enabled: !!id,
  });

  const { data: schedulePayload } = useQuery({
    queryKey: ['admin-astro-schedule', id],
    queryFn: async () => {
      const r = await getAstrologerSchedule(id!);
      return r.data?.data || r.data;
    },
    enabled: !!id && tab === 'availability',
  });

  const listTab =
    tab === 'consultations' ? 'consultations'
      : tab === 'earnings' ? 'transactions'
        : tab === 'reviews' ? 'reviews'
          : null;

  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['admin-astro-activity', id, listTab, page, typeFilter, statusFilter],
    queryFn: async () => {
      const params: any = { tab: listTab, page, limit: 15 };
      if (listTab === 'consultations') {
        if (typeFilter) params.type = typeFilter;
        if (statusFilter) params.status = statusFilter;
      }
      const r = await getAstrologerActivity(id!, params);
      return r.data;
    },
    enabled: !!id && !!listTab,
  });

  const approveMutation = useMutation({
    mutationFn: (approvalStatus: string) =>
      updateAstrologerApproval(id!, { approvalStatus, approvalNote }),
    onSuccess: (_, status) => {
      toast.success(`Astrologer ${status}`);
      setApproveModal(null);
      setApprovalNote('');
      qc.invalidateQueries({ queryKey: ['admin-astro-detail', id] });
      qc.invalidateQueries({ queryKey: ['admin-astrologers'] });
    },
    onError: () => toast.error('Approval update failed'),
  });

  useEffect(() => {
    const a = detail?.astrologer;
    if (!a) return;
    setPriceForm({
      chatPrice: String((a.chatPrice || 0) / 100),
      callPrice: String((a.callPrice || 0) / 100),
      videoPrice: String((a.videoPrice || 0) / 100),
      chatMinMinutes: String(a.chatMinMinutes || 5),
      callMinMinutes: String(a.callMinMinutes || 5),
      videoMinMinutes: String(a.videoMinMinutes || 10),
    });
  }, [detail?.astrologer]);

  const priceMutation = useMutation({
    mutationFn: () =>
      updateAstrologer(id!, {
        chatPrice: Math.round(Number(priceForm.chatPrice) * 100),
        callPrice: Math.round(Number(priceForm.callPrice) * 100),
        videoPrice: Math.round(Number(priceForm.videoPrice) * 100),
        chatMinMinutes: Math.max(1, Number(priceForm.chatMinMinutes) || 5),
        callMinMinutes: Math.max(1, Number(priceForm.callMinMinutes) || 5),
        videoMinMinutes: Math.max(1, Number(priceForm.videoMinMinutes) || 10),
      }),
    onSuccess: () => {
      toast.success('Astrologer pricing saved');
      qc.invalidateQueries({ queryKey: ['admin-astro-detail', id] });
      qc.invalidateQueries({ queryKey: ['admin-astrologers'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to save pricing'),
  });

  const credMutation = useMutation({
    mutationFn: () => {
      const pw = credPassword.trim();
      return setAstrologerCredentials(id!, pw ? { password: pw } : {});
    },
    onSuccess: (res) => {
      const data = res.data?.data;
      if (data?.loginId) setCredResult({ loginId: data.loginId, password: data.password });
      setCredModal(false);
      setCredPassword('');
      toast.success('Credentials updated');
      qc.invalidateQueries({ queryKey: ['admin-astro-detail', id] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed'),
  });

  const astro = detail?.astrologer;
  const stats = detail?.stats || {};
  const list = listData?.data || [];
  const pagination = listData?.pagination;

  if (!id) {
    return <div className="p-6"><EmptyState message="Astrologer not found" /></div>;
  }

  return (
    <div className="p-6">
      <div className="mb-4">
        <button
          onClick={() => navigate('/astrologers')}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={16} /> Back to Astrologers
        </button>
      </div>

      <PageHeader
        title={astro?.name || 'Astrologer Details'}
        subtitle="Track sessions, earnings, users served & reviews"
      >
        <button onClick={() => refetch()} className="btn-secondary btn-sm">
          <RefreshCw size={14} /> Refresh
        </button>
        <button onClick={() => setCredModal(true)} className="btn-secondary btn-sm">
          <KeyRound size={14} /> Reset password
        </button>
        {astro?.approvalStatus === 'pending' && (
          <>
            <button onClick={() => setApproveModal('approved')} className="btn-primary btn-sm">
              <CheckCircle size={14} /> Approve
            </button>
            <button onClick={() => setApproveModal('rejected')} className="btn-danger btn-sm">
              <XCircle size={14} /> Reject
            </button>
          </>
        )}
        {astro?.approvalStatus === 'approved' && (
          <button onClick={() => setApproveModal('suspended')} className="btn-danger btn-sm">
            <Ban size={14} /> Suspend
          </button>
        )}
        {(astro?.approvalStatus === 'rejected' || astro?.approvalStatus === 'suspended') && (
          <button onClick={() => setApproveModal('approved')} className="btn-primary btn-sm">
            <ShieldCheck size={14} /> Re-Approve
          </button>
        )}
      </PageHeader>

      {/* Hero */}
      <div className="card p-5 mb-5">
        {isLoading ? (
          <TableSkeleton rows={2} cols={4} />
        ) : astro ? (
          <div className="flex flex-col lg:flex-row lg:items-center gap-5">
            <div className="flex items-center gap-4 min-w-0">
              <Avatar
                name={astro.name}
                image={astro.profileImage}
                size={88}
                onClick={astro.profileImage ? () => setPhotoViewer(resolveMediaUrl(astro.profileImage)) : undefined}
              />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-xl font-bold text-gray-900 truncate">{astro.name}</h2>
                  <StatusBadge status={astro.availabilityStatus || 'offline'} />
                  <StatusBadge status={astro.approvalStatus} />
                  {astro.isVerified && <span className="badge-blue">Verified</span>}
                </div>
                {astro.profileImage && (
                  <button
                    type="button"
                    className="mt-1 text-xs font-semibold text-primary-600 hover:text-primary-700 inline-flex items-center gap-1"
                    onClick={() => setPhotoViewer(resolveMediaUrl(astro.profileImage))}
                  >
                    <Eye size={12} /> View profile photo
                  </button>
                )}
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
                  {astro.loginId && (
                    <span className="inline-flex items-center gap-1 font-mono text-primary-700 bg-primary-50 px-2 py-0.5 rounded">
                      ID: {astro.loginId}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1"><Phone size={13} /> {astro.phone}</span>
                  {astro.email && <span className="inline-flex items-center gap-1"><Mail size={13} /> {astro.email}</span>}
                  <span className="inline-flex items-center gap-1">
                    <Star size={13} className="text-amber-500" /> {(stats.avgRating || astro.rating || 0).toFixed(1)} ({stats.reviewCount || 0})
                  </span>
                  <span className="inline-flex items-center gap-1"><Briefcase size={13} /> {astro.experience || 0} yrs</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:ml-auto w-full lg:w-auto">
              <div className="bg-green-50 rounded-xl px-3 py-2 text-center">
                <p className="text-[11px] text-green-700 font-semibold">Earnings</p>
                <p className="text-base font-bold text-green-800">{formatCurrency(stats.totalEarnings || 0)}</p>
              </div>
              <div className="bg-blue-50 rounded-xl px-3 py-2 text-center">
                <p className="text-[11px] text-blue-700 font-semibold">Sessions</p>
                <p className="text-base font-bold text-blue-800">{stats.consultationCount || 0}</p>
              </div>
              <div className="bg-purple-50 rounded-xl px-3 py-2 text-center">
                <p className="text-[11px] text-purple-700 font-semibold">Talk Time</p>
                <p className="text-base font-bold text-purple-800">{formatDuration(stats.totalDurationSeconds || 0)}</p>
              </div>
              <div className="bg-orange-50 rounded-xl px-3 py-2 text-center">
                <p className="text-[11px] text-orange-700 font-semibold">Wallet</p>
                <p className="text-base font-bold text-orange-800">{formatCurrency(stats.walletBalance || 0)}</p>
              </div>
            </div>
          </div>
        ) : (
          <EmptyState message="Astrologer not found" />
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1.5 mb-5 border-b border-gray-200 pb-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setPage(1); }}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.key
                ? 'bg-primary-500 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === 'overview' && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            <StatTile label="Chat Sessions" value={stats.chatCount || 0} icon={<MessageSquare size={16} />} color="text-blue-600" />
            <StatTile label="Audio Sessions" value={stats.callCount || 0} icon={<PhoneCall size={16} />} color="text-green-600" />
            <StatTile label="Video Sessions" value={stats.videoCount || 0} icon={<Video size={16} />} color="text-purple-600" />
            <StatTile label="Total Talk Time" value={formatDuration(stats.totalDurationSeconds || 0)} icon={<Clock size={16} />} color="text-orange-600" />
            <StatTile label="Users Served" value={stats.uniqueUsers || 0} icon={<Users size={16} />} color="text-teal-600" />
            <StatTile label="Chat Messages" value={stats.messageCount || 0} icon={<MessageSquare size={16} />} color="text-indigo-600" />
            <StatTile label="Gross Revenue" value={formatCurrency(stats.grossRevenue || 0)} icon={<DollarSign size={16} />} color="text-emerald-600" />
            <StatTile label="Astrologer Earnings" value={formatCurrency(stats.totalEarnings || 0)} icon={<Wallet size={16} />} color="text-green-700" />
            <StatTile label="Completed" value={stats.endedCount || 0} icon={<CheckCircle size={16} />} color="text-green-700" />
            <StatTile label="Cancelled / Missed" value={stats.cancelledCount || 0} icon={<Ban size={16} />} color="text-red-600" />
            <StatTile label="Avg Rating" value={(stats.avgRating || 0).toFixed(1)} icon={<Star size={16} />} color="text-amber-600" sub={`${stats.reviewCount || 0} reviews`} />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            <div className="card overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900">Top Users Served</h3>
                <button onClick={() => setTab('consultations')} className="text-xs text-primary-600 font-semibold hover:underline">View all</button>
              </div>
              {!detail?.topUsers?.length ? (
                <div className="p-6"><EmptyState message="No consultations yet" icon={<Users size={28} />} /></div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {detail.topUsers.map((row: any) => (
                    <div key={String(row._id)} className="px-4 py-3 flex items-center gap-3">
                      <Avatar name={row.user?.name || row.user?.phone} image={row.user?.profileImage} size={40} />
                      <div className="flex-1 min-w-0">
                        <UserLink
                          entity={row.user || { _id: row._id }}
                          className="text-sm font-semibold text-primary-600 hover:underline truncate block"
                        >
                          {row.user?.name || row.user?.phone || 'User'}
                        </UserLink>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {row.sessions} sessions · {formatDuration(row.duration || 0)} · Chat {row.chat} · Audio {row.call}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">{formatCurrency(row.earnings || 0)}</p>
                        <p className="text-[11px] text-gray-400">{formatDate(row.lastSessionAt)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="card overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900">Recent Consultations</h3>
                <button onClick={() => setTab('consultations')} className="text-xs text-primary-600 font-semibold hover:underline">View all</button>
              </div>
              {!detail?.recentConsultations?.length ? (
                <div className="p-6"><EmptyState message="No sessions yet" icon={<MessageSquare size={28} />} /></div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {detail.recentConsultations.map((c: any) => (
                    <div key={c._id} className="px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center">
                        {TYPE_ICON[c.type] || <MessageSquare size={13} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <UserLink
                          entity={c.userId}
                          className="text-sm font-semibold text-primary-600 hover:underline truncate block"
                        >
                          {c.userId?.name || c.userId?.phone || 'User'}
                        </UserLink>
                        <p className="text-xs text-gray-500 capitalize">{c.type} · {formatDuration(c.duration || 0)}</p>
                      </div>
                      <StatusBadge status={c.status} />
                      <span className="text-sm font-semibold text-gray-800 w-20 text-right">{formatCurrency(c.astrologerEarnings || 0)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === 'availability' && (
        <div className="card p-5 space-y-4">
          {isLoading || !astro ? <TableSkeleton rows={4} cols={3} /> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <InfoCell label="Live status" value={astro.availabilityStatus} />
                <InfoCell label="Chat online" value={astro.isChatOnline ? 'Yes' : 'No'} />
                <InfoCell label="Audio online" value={astro.isCallOnline ? 'Yes' : 'No'} />
                <InfoCell label="Video online" value={astro.isVideoOnline ? 'Yes' : 'No'} />
              </div>
              <p className="text-xs text-gray-500">
                Working hours, breaks, vacation and appointment slots are managed by the astrologer in-app (PDF §20).
              </p>
              {schedulePayload?.schedule ? (
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 space-y-2">
                  <p className="text-xs font-semibold text-gray-700">
                    Weekly schedule · {schedulePayload.schedule.timezone || 'IST'} · slots {schedulePayload.schedule.slotDurationMinutes || 30}m
                  </p>
                  {(schedulePayload.schedule.weekly || []).map((w: any) => (
                    <div key={w.day} className="text-xs text-gray-600 flex gap-2">
                      <span className="w-16 font-medium capitalize">{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][w.day] || w.day}</span>
                      <span>{(w.slots || []).map((s: any) => `${s.start}–${s.end}`).join(', ') || '—'}</span>
                    </div>
                  ))}
                  {(schedulePayload.schedule.vacations || []).length > 0 && (
                    <p className="text-xs text-amber-700">
                      Vacations: {(schedulePayload.schedule.vacations || []).map((v: any) => `${formatDateTime(v.from)} → ${formatDateTime(v.to)}`).join('; ')}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No schedule saved yet.</p>
              )}
              <AstrologerAppointmentsSnippet astrologerId={id!} />
            </>
          )}
        </div>
      )}

      {tab === 'pricing' && (
        <div className="card p-5">
          {isLoading || !astro ? (
            <TableSkeleton rows={3} cols={3} />
          ) : (
            <form
              className="space-y-4 max-w-3xl"
              onSubmit={(e) => {
                e.preventDefault();
                priceMutation.mutate();
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-bold text-ink">Per-minute rates</h3>
                  <p className="text-xs text-ink/50 mt-0.5">
                    Edit chat / audio / video pricing for this astrologer. Amounts are in ₹ per minute.
                  </p>
                </div>
                <button type="submit" className="btn-primary btn-sm" disabled={priceMutation.isPending}>
                  <Save size={14} />
                  {priceMutation.isPending ? 'Saving…' : 'Save pricing'}
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-ink/60 mb-1">Chat ₹/min</label>
                  <input
                    type="number"
                    step="0.5"
                    min="1"
                    className="input"
                    value={priceForm.chatPrice}
                    onChange={(e) => setPriceForm({ ...priceForm, chatPrice: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-ink/60 mb-1">Audio ₹/min</label>
                  <input
                    type="number"
                    step="0.5"
                    min="1"
                    className="input"
                    value={priceForm.callPrice}
                    onChange={(e) => setPriceForm({ ...priceForm, callPrice: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-ink/60 mb-1">Video ₹/min</label>
                  <input
                    type="number"
                    step="0.5"
                    min="1"
                    className="input"
                    value={priceForm.videoPrice}
                    onChange={(e) => setPriceForm({ ...priceForm, videoPrice: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-ink/60 mb-1">Chat min minutes</label>
                  <input
                    type="number"
                    min="1"
                    className="input"
                    value={priceForm.chatMinMinutes}
                    onChange={(e) => setPriceForm({ ...priceForm, chatMinMinutes: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-ink/60 mb-1">Audio min minutes</label>
                  <input
                    type="number"
                    min="1"
                    className="input"
                    value={priceForm.callMinMinutes}
                    onChange={(e) => setPriceForm({ ...priceForm, callMinMinutes: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-ink/60 mb-1">Video min minutes</label>
                  <input
                    type="number"
                    min="1"
                    className="input"
                    value={priceForm.videoMinMinutes}
                    onChange={(e) => setPriceForm({ ...priceForm, videoMinMinutes: e.target.value })}
                  />
                </div>
              </div>
              <p className="text-xs text-ink/40">
                Preview: Chat {formatCurrency(Math.round(Number(priceForm.chatPrice || 0) * 100))}/min ·
                Audio {formatCurrency(Math.round(Number(priceForm.callPrice || 0) * 100))}/min ·
                Video {formatCurrency(Math.round(Number(priceForm.videoPrice || 0) * 100))}/min
              </p>
            </form>
          )}
        </div>
      )}

      {tab === 'withdrawals' && (
        <AstrologerWithdrawalsSnippet astrologerId={id!} />
      )}

      {/* Profile */}
      {tab === 'profile' && (
        <div className="card p-5">
          {isLoading ? <TableSkeleton rows={6} cols={3} /> : astro ? (
            <div className="space-y-5">
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3">Basic Info</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <InfoCell label="Full Name" value={astro.name} />
                  <InfoCell label="Display Name" value={astro.displayName || astro.name} />
                  <InfoCell label="Phone" value={astro.phone} />
                  <InfoCell label="Email" value={astro.email} />
                  <InfoCell label="Gender" value={astro.gender} />
                  <InfoCell label="Experience" value={`${astro.experience || 0} years`} />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-2">Bio</h3>
                <p className="text-sm text-gray-700 bg-gray-50 rounded-xl p-3">{astro.bio || 'No bio added.'}</p>
              </div>

              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3">Specialities & Languages</h3>
                <div className="flex flex-wrap gap-2 mb-3">
                  {(astro.specialities || []).map((s: string) => (
                    <span key={s} className="badge-orange capitalize">{s}</span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {(astro.languages || []).map((l: string) => (
                    <span key={l} className="badge-blue uppercase">{l}</span>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3">Pricing</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <InfoCell label="Chat Rate" value={`${formatCurrency(astro.chatPrice || 0)}/min`} />
                  <InfoCell label="Audio Rate" value={`${formatCurrency(astro.callPrice || 0)}/min`} />
                  <InfoCell label="Video Rate" value={`${formatCurrency(astro.videoPrice || 0)}/min`} />
                  <InfoCell label="Chat Min Mins" value={astro.chatMinMinutes || 5} />
                  <InfoCell label="Audio Min Mins" value={astro.callMinMinutes || 5} />
                  <InfoCell label="Video Min Mins" value={astro.videoMinMinutes || 10} />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3">Account Status</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <InfoCell label="Active" value={astro.isActive ? 'Yes' : 'No'} />
                  <InfoCell label="Verified" value={astro.isVerified ? 'Yes' : 'No'} />
                  <InfoCell label="Approval" value={astro.approvalStatus} />
                  <InfoCell label="Approval Note" value={astro.approvalNote || '—'} />
                  <InfoCell label="Availability" value={astro.availabilityStatus} />
                  <InfoCell label="Joined" value={formatDateTime(astro.createdAt)} />
                  <InfoCell label="Last Active" value={astro.lastActiveAt ? formatDateTime(astro.lastActiveAt) : '—'} />
                  <InfoCell label="Astrologer ID" value={<span className="font-mono text-xs">{astro._id}</span>} />
                  <InfoCell label="Pending Withdrawal" value={formatCurrency(astro.pendingPayout || 0)} />
                </div>
              </div>

              {astro.bankDetails && (astro.bankDetails.upiId || astro.bankDetails.accountNumber) && (
                <div>
                  <h3 className="text-sm font-bold text-gray-900 mb-3">Bank / Withdrawal Details</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <InfoCell label="Account Holder" value={astro.bankDetails.accountHolderName} />
                    <InfoCell label="Account Number" value={astro.bankDetails.accountNumber} />
                    <InfoCell label="IFSC" value={astro.bankDetails.ifsc} />
                    <InfoCell label="UPI ID" value={astro.bankDetails.upiId} />
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Consultations */}
      {tab === 'consultations' && (
        <div className="space-y-4">
          <div className="card p-3 flex flex-wrap gap-2 items-center">
            {[['','All'], ['chat','Chat'], ['call','Audio'], ['video','Video']].map(([v, l]) => (
              <button
                key={v || 'all'}
                onClick={() => { setTypeFilter(v); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${typeFilter === v ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              >
                {l}
              </button>
            ))}
            <div className="w-px h-5 bg-gray-200 mx-1" />
            {[['','All Status'], ['ended','Ended'], ['active','Active'], ['waiting','Waiting'], ['cancelled','Cancelled'], ['missed','Missed']].map(([v, l]) => (
              <button
                key={`s-${v || 'all'}`}
                onClick={() => { setStatusFilter(v); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${statusFilter === v ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              >
                {l}
              </button>
            ))}
          </div>

          <div className="card overflow-hidden">
            {listLoading ? (
              <div className="p-5"><TableSkeleton rows={8} cols={7} /></div>
            ) : list.length === 0 ? (
              <EmptyState message="No consultations found" icon={<MessageSquare size={32} />} />
            ) : (
              <>
                <Table>
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="table-th">#</th>
                      <th className="table-th">User</th>
                      <th className="table-th">Type</th>
                      <th className="table-th">Status</th>
                      <th className="table-th">Duration</th>
                      <th className="table-th">Messages</th>
                      <th className="table-th">User Paid</th>
                      <th className="table-th">Earnings</th>
                      <th className="table-th">Started</th>
                      <th className="table-th">Ended</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((c: any, idx: number) => (
                      <tr key={c._id} className="table-row">
                        <td className="table-td text-xs text-gray-400">{(page - 1) * 15 + idx + 1}</td>
                        <td className="table-td">
                          <div className="flex items-center gap-2">
                            <Avatar name={c.userId?.name || c.userId?.phone} image={c.userId?.profileImage} size={32} />
                            <div className="min-w-0">
                              <UserLink
                                entity={c.userId}
                                className="text-xs font-semibold text-primary-600 hover:underline truncate block"
                              >
                                {c.userId?.name || '—'}
                              </UserLink>
                              <Link to={`/consultations/${c._id}`} className="text-[11px] text-primary-600 hover:underline">Open chat / details</Link>
                              <p className="text-[11px] text-gray-400 font-mono">{c.userId?.phone}</p>
                            </div>
                          </div>
                        </td>
                        <td className="table-td">
                          <span className="inline-flex items-center gap-1 capitalize text-xs font-medium">
                            {TYPE_ICON[c.type]} {c.type === 'call' ? 'Audio' : c.type}
                          </span>
                        </td>
                        <td className="table-td"><StatusBadge status={c.status} /></td>
                        <td className="table-td text-xs font-semibold">{formatDuration(c.duration || 0)}</td>
                        <td className="table-td text-xs">{c.type === 'chat' ? (c.messageCount ?? 0) : '—'}</td>
                        <td className="table-td text-xs">{formatCurrency(c.totalAmount || c.reservedAmount || 0)}</td>
                        <td className="table-td text-xs font-bold text-green-700">{formatCurrency(c.astrologerEarnings || 0)}</td>
                        <td className="table-td text-xs whitespace-nowrap">{c.startedAt ? formatDateTime(c.startedAt) : formatDateTime(c.createdAt)}</td>
                        <td className="table-td text-xs whitespace-nowrap">{c.endedAt ? formatDateTime(c.endedAt) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {pagination && pagination.pages > 1 && (
                  <div className="p-4 border-t border-gray-100">
                    <Pagination page={page} pages={pagination.pages} total={pagination.total} limit={15} onPageChange={setPage} />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Earnings / Wallet */}
      {tab === 'earnings' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatTile label="Wallet Balance" value={formatCurrency(stats.walletBalance || 0)} icon={<Wallet size={16} />} color="text-green-600" />
            <StatTile label="Total Earnings" value={formatCurrency(stats.totalEarnings || 0)} icon={<DollarSign size={16} />} color="text-emerald-600" />
            <StatTile label="Pending Withdrawal" value={formatCurrency(stats.pendingPayout || 0)} icon={<Clock size={16} />} color="text-orange-600" />
          </div>

          <div className="card overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-900">Wallet Transactions</h3>
            </div>
            {listLoading ? (
              <div className="p-5"><TableSkeleton rows={8} cols={5} /></div>
            ) : list.length === 0 ? (
              <EmptyState message="No wallet transactions" icon={<Wallet size={32} />} />
            ) : (
              <>
                <Table>
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="table-th">#</th>
                      <th className="table-th">Type</th>
                      <th className="table-th">Category</th>
                      <th className="table-th">Description</th>
                      <th className="table-th">Amount</th>
                      <th className="table-th">Balance After</th>
                      <th className="table-th">Status</th>
                      <th className="table-th">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((t: any, idx: number) => (
                      <tr key={t._id} className="table-row">
                        <td className="table-td text-xs text-gray-400">{(page - 1) * 15 + idx + 1}</td>
                        <td className="table-td">
                          <span className={`badge ${t.type === 'credit' ? 'badge-green' : 'badge-red'} capitalize`}>{t.type}</span>
                        </td>
                        <td className="table-td text-xs capitalize">{String(t.category || '').replace(/_/g, ' ')}</td>
                        <td className="table-td text-xs max-w-[220px] truncate">{t.description}</td>
                        <td className={`table-td text-xs font-bold ${t.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                          {t.type === 'credit' ? '+' : '-'}{formatCurrency(t.amount || 0)}
                        </td>
                        <td className="table-td text-xs">{formatCurrency(t.balanceAfter || 0)}</td>
                        <td className="table-td"><StatusBadge status={t.status || 'completed'} /></td>
                        <td className="table-td text-xs whitespace-nowrap">{formatDateTime(t.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {pagination && pagination.pages > 1 && (
                  <div className="p-4 border-t border-gray-100">
                    <Pagination page={page} pages={pagination.pages} total={pagination.total} limit={15} onPageChange={setPage} />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Reviews */}
      {tab === 'reviews' && (
        <div className="space-y-4">
          <div className="card p-4">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Rating Breakdown</h3>
            <div className="grid grid-cols-5 gap-2">
              {[5, 4, 3, 2, 1].map((star) => (
                <div key={star} className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-500 inline-flex items-center gap-1 justify-center">
                    <Star size={11} className="text-amber-500" fill="currentColor" /> {star}
                  </p>
                  <p className="text-lg font-bold text-gray-900 mt-1">{stats.ratingBreakdown?.[star] || 0}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="card overflow-hidden">
            {listLoading ? (
              <div className="p-5"><TableSkeleton rows={6} cols={4} /></div>
            ) : list.length === 0 ? (
              <EmptyState message="No reviews yet" icon={<Star size={32} />} />
            ) : (
              <>
                <Table>
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="table-th">#</th>
                      <th className="table-th">User</th>
                      <th className="table-th">Rating</th>
                      <th className="table-th">Comment</th>
                      <th className="table-th">Tags</th>
                      <th className="table-th">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((r: any, idx: number) => (
                      <tr key={r._id} className="table-row">
                        <td className="table-td text-xs text-gray-400">{(page - 1) * 15 + idx + 1}</td>
                        <td className="table-td">
                          <div className="flex items-center gap-2">
                            <Avatar name={r.userId?.name || r.userId?.phone} image={r.userId?.profileImage} size={28} />
                            <UserLink
                              entity={r.userId}
                              className="text-xs font-semibold text-primary-600 hover:underline"
                            >
                              {r.userId?.name || r.userId?.phone || '—'}
                            </UserLink>
                          </div>
                        </td>
                        <td className="table-td">
                          <span className="inline-flex items-center gap-1 text-amber-600 font-bold text-xs">
                            <Star size={12} fill="currentColor" /> {r.rating}
                          </span>
                        </td>
                        <td className="table-td text-xs max-w-[280px]">{r.comment || '—'}</td>
                        <td className="table-td">
                          <div className="flex flex-wrap gap-1">
                            {(r.tags || []).map((tag: string) => (
                              <span key={tag} className="badge-gray text-[10px]">{tag}</span>
                            ))}
                          </div>
                        </td>
                        <td className="table-td text-xs whitespace-nowrap">{formatDateTime(r.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {pagination && pagination.pages > 1 && (
                  <div className="p-4 border-t border-gray-100">
                    <Pagination page={page} pages={pagination.pages} total={pagination.total} limit={15} onPageChange={setPage} />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <Modal
        open={!!approveModal}
        onClose={() => { setApproveModal(null); setApprovalNote(''); }}
        title={`${approveModal === 'approved' ? 'Approve' : approveModal === 'rejected' ? 'Reject' : 'Suspend'} Astrologer`}
        size="sm"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            You are about to <strong>{approveModal}</strong> <strong>{astro?.name}</strong>.
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Note (optional)</label>
            <textarea rows={2} className="input resize-none" value={approvalNote} onChange={(e) => setApprovalNote(e.target.value)} placeholder="Reason or note…" />
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setApproveModal(null); setApprovalNote(''); }} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button
              onClick={() => approveMutation.mutate(approveModal!)}
              disabled={approveMutation.isPending}
              className={`flex-1 justify-center ${approveModal === 'approved' ? 'btn-primary' : 'btn-danger'}`}
            >
              {approveMutation.isPending ? '…' : `Confirm ${approveModal}`}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={credModal} onClose={() => setCredModal(false)} title="Reset astrologer password" size="sm">
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Login ID: <strong className="font-mono">{astro?.loginId || 'will be generated'}</strong>
          </p>
          <label className="text-sm block">New password
            <input
              className="input mt-1"
              value={credPassword}
              onChange={(e) => setCredPassword(e.target.value)}
              placeholder="Leave blank to auto-generate a unique password"
            />
          </label>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setCredModal(false)}>Cancel</button>
            <button className="btn-primary flex-1" disabled={credMutation.isPending} onClick={() => credMutation.mutate()}>
              {credMutation.isPending ? 'Saving…' : 'Update'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={!!credResult} onClose={() => setCredResult(null)} title="Credentials" size="sm">
        {credResult && (
          <div className="space-y-3">
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Password shown once — copy and share with the astrologer.
            </p>
            <div className="flex justify-between items-center bg-gray-50 rounded-lg px-3 py-2">
              <div><div className="text-xs text-gray-400">Astrologer ID</div><div className="font-mono font-semibold">{credResult.loginId}</div></div>
              <button className="btn-secondary" onClick={() => { navigator.clipboard.writeText(credResult.loginId); toast.success('Copied'); }}><Copy size={14} /></button>
            </div>
            <div className="flex justify-between items-center bg-gray-50 rounded-lg px-3 py-2">
              <div><div className="text-xs text-gray-400">Password</div><div className="font-mono font-semibold">{credResult.password}</div></div>
              <button className="btn-secondary" onClick={() => { navigator.clipboard.writeText(credResult.password); toast.success('Copied'); }}><Copy size={14} /></button>
            </div>
            <button className="btn-primary w-full" onClick={() => setCredResult(null)}>Done</button>
          </div>
        )}
      </Modal>

      <ImageViewer
        open={!!photoViewer}
        src={photoViewer || ''}
        alt={astro?.name || 'Profile'}
        onClose={() => setPhotoViewer(null)}
      />
    </div>
  );
}

function AstrologerAppointmentsSnippet({ astrologerId }: { astrologerId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-astro-appointments', astrologerId],
    queryFn: () => getAdminAppointments({ astrologerId, limit: 10 }).then((r) => r.data.data),
  });
  const rows = data?.data || [];
  return (
    <div>
      <h3 className="text-sm font-bold text-gray-900 mb-2">Recent appointments</h3>
      {isLoading ? <TableSkeleton rows={3} cols={3} /> : rows.length === 0 ? (
        <EmptyState message="No appointments" icon={<Calendar size={24} />} />
      ) : (
        <Table>
          <thead className="bg-gray-50 border-b"><tr>
            <th className="table-th">When</th><th className="table-th">Type</th><th className="table-th">Status</th>
          </tr></thead>
          <tbody>
            {rows.map((a: any) => (
              <tr key={a._id} className="table-row">
                <td className="table-td text-xs">{formatDateTime(a.scheduledAt)}</td>
                <td className="table-td text-xs capitalize">{a.type === 'call' ? 'Audio' : a.type}</td>
                <td className="table-td"><StatusBadge status={a.status} /></td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

function AstrologerWithdrawalsSnippet({ astrologerId }: { astrologerId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-astro-withdrawals', astrologerId],
    queryFn: () => getAdminWithdrawals({ limit: 20 }).then((r) => r.data.data),
  });
  const rows = (data?.data || []).filter((w: any) => {
    const id = w.astrologerId?._id || w.astrologerId;
    return String(id) === String(astrologerId);
  });
  return (
    <div className="card overflow-hidden">
      {isLoading ? (
        <div className="p-5"><TableSkeleton rows={5} cols={4} /></div>
      ) : rows.length === 0 ? (
        <EmptyState message="No withdrawal requests" icon={<CreditCard size={32} />} />
      ) : (
        <Table>
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="table-th">Amount</th>
              <th className="table-th">Status</th>
              <th className="table-th">Ref</th>
              <th className="table-th">Requested</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((w: any) => (
              <tr key={w._id} className="table-row">
                <td className="table-td font-semibold">{formatCurrency(w.amount || 0)}</td>
                <td className="table-td"><StatusBadge status={w.status} /></td>
                <td className="table-td text-xs">{w.settlementReference || '—'}</td>
                <td className="table-td text-xs">{formatDateTime(w.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
